<template>
  <div class="online-cashfloat-closure" data-cy="online-cashfloat-closure">
    <SyncInProgress v-if="showSyncModal" @sync-success="handleSyncSuccess" />

    <!-- Main Content -->
    <main class="main-content">
      <div class="d-flex fl-s-b fl-a-c">
        <div class="title-container">
          <div class="d-flex fl-s-b fl-a-c">
            <h1 class="title">{{ $t("cashfloat.sale-closure") }}</h1>
            <button
              class="watch-video-btn"
              @click="toggleYoutubeDemoModal(true)"
            >
              <span>Watch video</span>
              <YoutubeIcon class="info-icon" />
            </button>
          </div>

          <p class="session-info">
            {{ sessionOpenedMessagee }}
          </p>
        </div>

        <div class="action-buttons">
          <button
            v-if="hasLocation"
            class="btn btn-outline"
            @click="addCashTransfer"
            data-cy="add-cash-transfer-button"
          >
            <ArrowUpDownIcon class="btn-icon" />
            {{ $t("cashfloat.cash-transfer") }}
          </button>
          <button
            class="btn btn-primary"
            @click="openCashDrawer"
            data-cy="open-cash-drawer-button"
          >
            <RadioReceiverIcon class="btn-icon" />
            {{ $t("button.open-drawer") }}
          </button>
        </div>
      </div>

      <div class="tabs">
        <button
          :class="['tab-button', { active: activeTab === 'manual' }]"
          @click="switchTab('manual')"
          data-cy="manual-methods-button"
        >
          {{ $t("cashfloat.manual-methods") }}
        </button>
        <button
          :class="['tab-button', { active: activeTab === 'digital' }]"
          @click="switchTab('digital')"
          data-cy="digital-methods-button"
        >
          {{ $t("cashfloat.digital-methods") }}
        </button>
      </div>

      <div
        v-if="isLoading"
        class="loading-container"
        data-cy="loading-indicator"
      >
        <p>{{ $t("common.loading") }}...</p>
      </div>

      <div
        v-if="!isLoading && activeTab === 'manual' && paymentMethods.length"
        class="content-row"
      >
        <!-- Left Column: Currency details panel.
             Cash rounding is a derived adjustment rather than a tender the
             operator counts, so there is nothing to reconcile against it. -->
        <div
          class="cash-details-panel"
          v-if="
            selectedPayment &&
            selectedCurrency &&
            selectedPayment.name !== PaymentMethods.METHOD_CASH_ROUNDING
          "
          data-cy="payment-method-details-panel"
          :class="`details-panel-${selectedPayment.name}`"
        >
          <div class="panel-header">
            <div class="panel-title large-title">
              <FileSpreadsheetIcon class="panel-icon" />
              {{
                $t("payment-method-in-currency", {
                  method: $t(`payment-method.${selectedPayment.name}`),
                  currency: selectedCurrency,
                })
              }}
            </div>
            <button
              class="btn btn-outline btn-white btn-small font-weight-700"
              data-cy="cash-inventory-button"
              @click="openCashInventory(selectedCurrency)"
            >
              <BanknoteIcon class="btn-icon" />
              {{ $t("cashfloat.cash-inventory") }}
            </button>
          </div>

          <div
            v-if="
              selectedPayment.name === PaymentMethods.METHOD_POS_CASH &&
              selectedCurrency === groupCurrency
            "
            class="details-list"
          >
            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.payments") }}
              </span>
              <span class="detail-value payments-amount">{{
                formatCurrencyWithSymbol(
                  getCustomerAmountIn(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.refunds") }}
              </span>
              <span class="detail-value refunds-amount">{{
                formatCurrencyWithSymbol(
                  getRefund(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.change") }}
              </span>
              <span class="detail-value change-amount">{{
                formatCurrencyWithSymbol(
                  getChange(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-value">
                {{ t("cashfloat.settlement") }}
              </span>
              <span class="detail-value settlement-amount">{{
                formatCurrencyWithSymbol(
                  getSettlement(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item mt-2.5">
              <span class="detail-label">
                {{ t("cashfloat.initial-amount") }}
              </span>
              <span class="detail-value initial-amount">
                {{
                  formatCurrencyWithSymbol(
                    getInitialAmount(selectedPayment, selectedCurrency),
                    selectedCurrency as Currencies
                  )
                }}</span
              >
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.settlement") }}
              </span>
              <span class="detail-value settlement-amount">{{
                formatCurrencyWithSymbol(
                  getSettlement(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div
              v-if="
                hasLocation &&
                selectedPayment.name === PaymentMethods.METHOD_POS_CASH
              "
              class="detail-item"
            >
              <span class="detail-label">
                {{ t("cashfloat.transfer-in") }}
              </span>
              <span class="detail-value transfer-in">{{
                formatCurrencyWithSymbol(
                  getTransferIn(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div
              v-if="
                hasLocation &&
                selectedPayment.name === PaymentMethods.METHOD_POS_CASH
              "
              class="detail-item"
            >
              <span class="detail-label">
                {{ t("cashfloat.transfer-out") }}
              </span>
              <span class="detail-value transfer-out">{{
                formatCurrencyWithSymbol(
                  getTransferOut(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item mt-2.5">
              <span class="detail-value">
                {{ t("cashfloat.expected-amount") }}
              </span>
              <span class="detail-value expected-amount">{{
                formatCurrencyWithSymbol(
                  getExpectedAmount(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-value">
                {{ t("cashfloat.counted-amount") }}
              </span>
              <span class="detail-value counted-amount">{{
                formatCurrencyWithSymbol(
                  getCountedAmount(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span
                :class="[
                  `detail-value`,
                  !getDifference(selectedPayment, selectedCurrency)
                    ? 'green'
                    : 'red',
                ]"
              >
                {{ t("cashfloat.difference") }}
              </span>
              <span
                :class="[
                  `detail-value`,
                  'difference-amount',
                  !getDifference(selectedPayment, selectedCurrency)
                    ? 'green'
                    : 'red',
                ]"
                >{{
                  formatCurrencyWithSymbol(
                    getDifference(selectedPayment, selectedCurrency),
                    selectedCurrency as Currencies
                  )
                }}</span
              >
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.note") }}
              </span>
              <span class="detail-value note">{{
                !getNote(selectedPayment, selectedCurrency)
                  ? "-"
                  : getNote(selectedPayment, selectedCurrency)
              }}</span>
            </div>
          </div>
          <div v-else class="details-list">
            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.initial-amount") }}
              </span>
              <span class="detail-value initial-amount">
                {{
                  formatCurrencyWithSymbol(
                    getInitialAmount(selectedPayment, selectedCurrency),
                    selectedCurrency as Currencies
                  )
                }}</span
              >
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.payments") }}
              </span>
              <span class="detail-value payments-amount">{{
                formatCurrencyWithSymbol(
                  getCustomerAmountIn(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.refunds") }}
              </span>
              <span class="detail-value refunds-amount">{{
                formatCurrencyWithSymbol(
                  getRefund(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.change") }}
              </span>
              <span class="detail-value change-amount">{{
                formatCurrencyWithSymbol(
                  getChange(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.settlement") }}
              </span>
              <span class="detail-value settlement-amount">{{
                formatCurrencyWithSymbol(
                  getSettlement(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div
              v-if="
                hasLocation &&
                selectedPayment.name === PaymentMethods.METHOD_POS_CASH
              "
              class="detail-item"
            >
              <span class="detail-label">
                {{ t("cashfloat.transfer-in") }}
              </span>
              <span class="detail-value transfer-in">{{
                formatCurrencyWithSymbol(
                  getTransferIn(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div
              v-if="
                hasLocation &&
                selectedPayment.name === PaymentMethods.METHOD_POS_CASH
              "
              class="detail-item"
            >
              <span class="detail-label">
                {{ t("cashfloat.transfer-out") }}
              </span>
              <span class="detail-value transfer-out">{{
                formatCurrencyWithSymbol(
                  getTransferOut(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-value">
                {{ t("cashfloat.expected-amount") }}
              </span>
              <span class="detail-value expected-amount">{{
                formatCurrencyWithSymbol(
                  getExpectedAmount(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span class="detail-value">
                {{ t("cashfloat.counted-amount") }}
              </span>
              <span class="detail-value counted-amount">{{
                formatCurrencyWithSymbol(
                  getCountedAmount(selectedPayment, selectedCurrency),
                  selectedCurrency as Currencies
                )
              }}</span>
            </div>

            <div class="detail-item">
              <span
                :class="[
                  `detail-value`,
                  !getDifference(selectedPayment, selectedCurrency)
                    ? 'green'
                    : 'red',
                ]"
              >
                {{ t("cashfloat.difference") }}
              </span>
              <span
                :class="[
                  `detail-value`,
                  'difference-amount',
                  !getDifference(selectedPayment, selectedCurrency)
                    ? 'green'
                    : 'red',
                ]"
                >{{
                  formatCurrencyWithSymbol(
                    getDifference(selectedPayment, selectedCurrency),
                    selectedCurrency as Currencies
                  )
                }}</span
              >
            </div>

            <div class="detail-item">
              <span class="detail-label">
                {{ t("cashfloat.note") }}
              </span>
              <span class="detail-value note">{{
                !getNote(selectedPayment, selectedCurrency)
                  ? "-"
                  : getNote(selectedPayment, selectedCurrency)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Right Column: Currency panels -->
        <div class="cash-panel" ref="currencyPanels">
          <div
            v-for="paymentMethod in paymentMethods"
            :key="paymentMethod.name"
            class="mb-2.5 summary-panel"
            :class="`summary-panel-${paymentMethod.name}`"
          >
            <!-- Currency sections are displayed only on manual tab or if they have values -->
            <div v-for="currency in activeCurrencies" :key="currency">
              <div
                v-if="
                  paymentMethod.name === PaymentMethods.METHOD_POS_CASH ||
                  currency === groupCurrency
                "
                class="currency-section"
                :class="`currency-section-${currency}`"
                :id="`currency-section-${paymentMethod.name}-${currency}`"
              >
                <div class="panel-header with-badge">
                  <div class="panel-title">
                    <component
                      v-if="getCurrencyIcon(currency)"
                      :is="getCurrencyIcon(currency)"
                      class="info-icon"
                    />
                    {{
                      $t("payment-method-in-currency", {
                        method: $t(`payment-method.${paymentMethod.name}`),
                        currency: currency,
                      })
                    }}
                  </div>
                  <span
                    :class="`status-badge ${
                      !getDifference(paymentMethod, currency)
                        ? 'matched'
                        : 'unmatched'
                    }`"
                  >
                    {{
                      !getDifference(paymentMethod, currency)
                        ? $t("cashfloat.match")
                        : $t("cashfloat.mismatch")
                    }}
                  </span>
                </div>

                <div class="currency-content">
                  <div class="currency-row">
                    <div class="w-1/2">
                      <div class="currency-label">
                        {{ t("cashfloat.initial-amount") }}
                      </div>
                      <div class="currency-value">
                        {{
                          formatCurrencyWithSymbol(
                            getInitialAmount(paymentMethod, currency),
                            currency as Currencies
                          )
                        }}
                      </div>
                    </div>

                    <div class="w-1/2">
                      <div class="currency-label">
                        {{ t("cashfloat.difference") }}
                      </div>
                      <div
                        :class="[
                          'currency-value',
                          !getDifference(paymentMethod, currency) ? '' : 'red',
                        ]"
                      >
                        {{
                          formatCurrencyWithSymbol(
                            getDifference(paymentMethod, currency),
                            currency as Currencies
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <div class="currency-row">
                    <div class="currency-label">
                      {{ t("cashfloat.counted-amount") }}
                    </div>
                  </div>

                  <div class="amount-input-container">
                    <input
                      type="text"
                      class="amount-input"
                      data-cy="counted-amount-input"
                      v-model="
                        forMattedCountedAmounts[paymentMethod.name][currency]
                      "
                      @blur="onCountedAmountBlur(paymentMethod, currency)"
                      @focus="handleFocus"
                    />

                    <div class="input-arrows">
                      <button
                        class="arrow-up"
                        @click="incrementAmount(paymentMethod, currency)"
                      >
                        <ChevronUpIcon />
                      </button>
                      <button
                        class="arrow-down"
                        @click="decrementAmount(paymentMethod, currency)"
                      >
                        <ChevronDownIcon />
                      </button>
                    </div>
                  </div>

                  <div class="action-buttons mt-4">
                    <button
                      :class="[
                        'btn btn-outline btn-full btn-white',
                        paymentMethod.name === selectedPayment?.name &&
                        currency === selectedCurrency
                          ? 'btn-selected'
                          : '',
                      ]"
                      @click="viewDetails(paymentMethod, currency)"
                      data-cy="view-details-button"
                    >
                      <FileSpreadsheetIcon class="btn-icon" />
                      {{ $t("button.view-details") }}
                    </button>
                    <button
                      :class="[
                        'btn btn-outline btn-full btn-white',
                        getDifference(paymentMethod, currency) &&
                        !getNote(paymentMethod, currency)
                          ? 'btn-error'
                          : '',
                      ]"
                      @click="openNoteDialog(paymentMethod, currency)"
                      data-cy="add-note-button"
                    >
                      <span
                        :id="`note-content-${paymentMethod.name}-${currency}`"
                        class="note-content"
                      >
                        <NotebookPenIcon class="btn-icon" />
                        {{ $t("cashfloat.add-note") }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Digital payment summary instead of cash details on digital tab -->
      <div
        v-if="!isLoading && activeTab === 'digital'"
        class="digital-details-panel digital-methods"
        data-cy="digital-summary-panel"
      >
        <div class="panel-header">
          <div class="panel-title">
            {{ $t("cashfloat.digital-methods-summary") }}
          </div>
        </div>

        <div v-if="digitalSummary" class="details-list">
          <div class="detail-item border-bottom">
            <p class="detail-value w-1/2">
              {{ t("cashfloat.payment-methods") }}
            </p>
            <div class="w-1/2 d-flex fl-s-b fl-a-c">
              <span class="detail-value">{{ $t("cashfloat.payments") }}</span>
              <span class="detail-value">{{ $t("cashfloat.refunds") }}</span>
              <span class="detail-value">{{ $t("cashfloat.settlement") }}</span>
            </div>
          </div>

          <div class="detail-item border-bottom">
            <p class="detail-value w-1/2">{{ t("cashfloat.credit-cards") }}</p>
            <div class="w-1/2 d-flex fl-s-b fl-a-c">
              <span
                class="detail-value font-weight-500"
                data-cy="credit-cards-payment"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalCreditCard.payment,
                    groupCurrency
                  )
                }}
              </span>
              <span
                class="detail-value font-weight-500"
                data-cy="credit-cards-refund"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalCreditCard.refund,
                    groupCurrency
                  )
                }}
              </span>
              <span
                class="detail-value font-weight-500"
                data-cy="credit-cards-settlement"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalCreditCard.payment +
                      digitalSummary.totalCreditCard.refund,
                    groupCurrency
                  )
                }}
              </span>
            </div>
          </div>

          <div class="detail-item border-bottom">
            <p class="detail-value w-1/2">{{ t("cashfloat.vouchers") }}</p>
            <div class="w-1/2 d-flex fl-s-b fl-a-c">
              <span
                class="detail-value font-weight-500"
                data-cy="vouchers-payment"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalVoucher.payment,
                    groupCurrency
                  )
                }}
              </span>
              <span
                class="detail-value font-weight-500"
                data-cy="vouchers-refund"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalVoucher.refund,
                    groupCurrency
                  )
                }}
              </span>
              <span
                class="detail-value font-weight-500"
                data-cy="vouchers-settlement"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalVoucher.payment +
                      digitalSummary.totalVoucher.refund,
                    groupCurrency
                  )
                }}
              </span>
            </div>
          </div>

          <div class="detail-item border-bottom">
            <p class="detail-value w-1/2">{{ t("cashfloat.gift-cards") }}</p>
            <div class="w-1/2 d-flex fl-s-b fl-a-c">
              <span
                class="detail-value font-weight-500"
                data-cy="gift-cards-payment"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalGiftCard.payment,
                    groupCurrency
                  )
                }}
              </span>
              <span
                class="detail-value font-weight-500"
                data-cy="gift-cards-refund"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalGiftCard.refund,
                    groupCurrency
                  )
                }}
              </span>
              <span
                class="detail-value font-weight-500"
                data-cy="gift-cards-settlement"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.totalGiftCard.payment +
                      digitalSummary.totalGiftCard.refund,
                    groupCurrency
                  )
                }}
              </span>
            </div>
          </div>

          <div class="detail-item">
            <p class="detail-value w-1/2 green">{{ t("field.total") }}</p>
            <div class="w-1/2 d-flex fl-s-b fl-a-c">
              <span class="detail-value green" data-cy="digital-total-payment">
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.total.payment,
                    groupCurrency
                  )
                }}
              </span>
              <span class="detail-value green" data-cy="digital-total-refund">
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.total.refund,
                    groupCurrency
                  )
                }}
              </span>
              <span
                class="detail-value green"
                data-cy="digital-total-settlement"
              >
                {{
                  formatCurrencyWithSymbol(
                    digitalSummary.total.payment + digitalSummary.total.refund,
                    groupCurrency
                  )
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <div class="w-full d-flex j-s-b fl-a-s closure-footer">
      <div class="warning-text">
        <TriangleAlertIcon class="warning-icon" />
        <p>
          {{ t("cashfloat.closure-warning-message") }}
        </p>
      </div>

      <!-- Footer Buttons -->
      <div class="footer-buttons">
        <button
          class="btn btn-outline btn-large"
          @click="cancelAndReturn"
          data-cy="cancel-button"
          :disabled="isSubmitting || isLoading"
        >
          <CircleXIcon class="btn-icon" />
          {{ t("cashfloat.cancel-return") }}
        </button>
        <button
          class="btn btn-primary btn-large"
          @click="acceptAndClose"
          data-cy="accept-button"
          :disabled="isSubmitting || isLoading"
        >
          <CheckCheckIcon class="btn-icon" />
          {{ t("cashfloat.accept-close") }}
        </button>
      </div>
    </div>

    <!-- Note modal -->
    <div
      style="width: 100%"
      v-if="showNoteDialog && selectedPaymentForNote && selectedCurrencyForNote"
      data-cy="add-note-modal"
    >
      <SDialog v-model:open="showNoteDialog">
        <SDialogContent
          class="online-cashfloat-closure !w-[30%] !max-w-[30%]"
          @open-auto-focus.prevent
          @escape-key-down.prevent
          @pointer-down-outside.prevent
          @focus-outside.prevent
        >
          <SDialogHeader>
            <SDialogTitle>{{ t("cashfloat.add-note") }}</SDialogTitle>
          </SDialogHeader>
          <div>
            <SInputGroup>
              <SInputGroupTextarea
                :model-value="note"
                @update:model-value="(v) => (note = v)"
                :rows="3"
                :placeholder="t('cashfloat.write-your-note-here')"
                data-cy="note-textarea"
              />
            </SInputGroup>
          </div>
          <SDialogFooter>
            <SButton
              type="button"
              variant="outline"
              data-cy="cancel-note-button"
              @click="showNoteDialog = false"
            >
              {{ t("button.cancel") }}
            </SButton>
            <SButton
              type="button"
              data-cy="confirm-note-button"
              @click="addNote(selectedPaymentForNote, selectedCurrencyForNote)"
            >
              {{ t("button.confirm") }}
            </SButton>
          </SDialogFooter>
        </SDialogContent>
      </SDialog>
    </div>

    <!-- Cash inventory modal -->
    <div
      v-if="showCashInventoryDialog && selectedDetailedCurrency"
      class="cash-inventory-modal"
    >
      <SDialog v-model:open="showCashInventoryDialog">
        <SDialogContent
          class="online-cashfloat-closure !w-[50%] !max-w-[50%]"
          @escape-key-down.prevent
          @pointer-down-outside.prevent
          @focus-outside.prevent
        >
          <SDialogHeader>
            <SDialogTitle class="sr-only">
              {{
                $t("payment-method-in-currency", {
                  method: $t("payment-method.CASH"),
                  currency: selectedDetailedCurrency.label,
                })
              }}
            </SDialogTitle>
          </SDialogHeader>
          <div
            style="
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-self: center;
            "
            data-cy="cash-inventory-modal"
          >
            <div style="height: 54vh; overflow-y: scroll; padding: 10px">
              <div>
                <div>
                  <p style="font-size: 35px">
                    {{
                      $t("payment-method-in-currency", {
                        method: $t("payment-method.CASH"),
                        currency: selectedDetailedCurrency.label,
                      })
                    }}
                  </p>
                </div>
                <div
                  style="
                    margin-top: 2%;
                    margin-bottom: 2%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                  "
                >
                  <div
                    style="
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      border-bottom: 2px solid #ededed;
                      color: #a6a6a6;
                    "
                  >
                    <div style="width: 20%; text-align: left">
                      <p>{{ t("cashfloat.bills") }}</p>
                    </div>
                    <div style="width: 20%; text-align: left">
                      <p>{{ t("cashfloat.amount") }}</p>
                    </div>
                    <div style="width: 58%; text-align: right">
                      <p>{{ t("cashfloat.value") }}</p>
                    </div>
                  </div>
                  <div
                    v-for="(bill, index) in selectedDetailedCurrency.bills"
                    :key="index"
                    style="
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      border-bottom: 1px solid #ededed;
                      padding-bottom: 1%;
                      padding-top: 1%;
                    "
                  >
                    <div style="width: 20%; text-align: left">
                      <p>
                        <strong>{{ bill.name }}</strong>
                      </p>
                    </div>
                    <div style="width: 20%; text-align: left">
                      <SInput
                        class="h-[3.25rem] text-base"
                        :model-value="bill.userInput"
                        @update:model-value="(v) => (bill.userInput = v)"
                        type="number"
                        :min="0"
                        @focus="handleInputFocus"
                        @blur="handleInputBlur"
                        :data-cy="`cash-bill-input-${bill.name}`"
                      />
                    </div>
                    <div style="width: 58%; text-align: right">
                      <p>
                        <strong>
                          {{
                            selectedDetailedCurrency.symbol +
                            " " +
                            (Number(bill.name) * (bill.userInput || 0))
                              .toLocaleString("en-EN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                              .split(",")
                              .join("'")
                          }}
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>
                <hr />
                <div
                  style="
                    margin-top: 3%;
                    margin-bottom: 2%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                  "
                >
                  <div
                    style="
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      border-bottom: 2px solid #ededed;
                      color: #a6a6a6;
                    "
                  >
                    <div style="width: 20%; text-align: left">
                      <p>{{ t("cashfloat.coins") }}</p>
                    </div>
                    <div style="width: 20%; text-align: left">
                      <p>{{ t("cashfloat.amount") }}</p>
                    </div>
                    <div style="width: 58%; text-align: right">
                      <p>{{ t("cashfloat.value") }}</p>
                    </div>
                  </div>
                  <div
                    v-for="(coin, index) in selectedDetailedCurrency.coins"
                    :key="index"
                    style="
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      border-bottom: 1px solid #ededed;
                      padding-bottom: 1%;
                      padding-top: 1%;
                    "
                  >
                    <div style="width: 20%; text-align: left">
                      <p>
                        <strong>{{ coin.name }}</strong>
                      </p>
                    </div>
                    <div style="width: 20%; text-align: left">
                      <SInput
                        class="h-[3.25rem] text-base"
                        :model-value="coin.userInput"
                        @update:model-value="(v) => (coin.userInput = v)"
                        type="number"
                        :min="0"
                        @focus="handleInputFocus"
                        @blur="handleInputBlur"
                        :data-cy="`cash-coin-input-${coin.name}`"
                      />
                    </div>
                    <div style="width: 58%; text-align: right">
                      <p>
                        <strong>{{
                          selectedDetailedCurrency.symbol +
                          " " +
                          (Number(coin.name) * (coin.userInput || 0))
                            .toLocaleString("en-EN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            .split(",")
                            .join("'")
                        }}</strong>
                      </p>
                    </div>
                  </div>
                  <div
                    v-for="(cent, index) in selectedDetailedCurrency.cents"
                    :key="index"
                    style="
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      border-bottom: 1px solid #ededed;
                      padding-bottom: 1%;
                      padding-top: 1%;
                    "
                  >
                    <div style="width: 20%; text-align: left">
                      <p>
                        <strong
                          >{{ cent.name
                          }}<span style="color: #ededed">
                            {{ t("cashfloat.cents") }}</span
                          ></strong
                        >
                      </p>
                    </div>
                    <div style="width: 20%; text-align: left">
                      <SInput
                        class="h-[3.25rem] text-base"
                        :model-value="cent.userInput"
                        @update:model-value="(v) => (cent.userInput = v)"
                        type="number"
                        :min="0"
                        @focus="handleInputFocus"
                        @blur="handleInputBlur"
                        :data-cy="`cash-cent-input-${cent.name}`"
                      />
                    </div>
                    <div style="width: 58%; text-align: right">
                      <p>
                        <strong>{{
                          selectedDetailedCurrency.symbol +
                          " " +
                          ((Number(cent.name) * (cent.userInput || 0)) / 100)
                            .toLocaleString("en-EN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            .split(",")
                            .join("'")
                        }}</strong>
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  style="
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    padding-top: 1.5%;
                    padding-bottom: 2.5%;
                  "
                >
                  <div class="f-medium">
                    <p class="font-weight-700">{{ t("field.total") }}</p>
                  </div>
                  <div>
                    <p class="font-weight-700">
                      {{
                        (sumDetailedCurrency(selectedDetailedCurrency) / 100)
                          .toLocaleString("en-EN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          .split(",")
                          .join("'")
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              style="
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                border-top: 1px solid gray;
                margin-top: 3%;
                padding-top: 1.5%;
              "
            >
              <SButton
                type="button"
                variant="outline"
                size="sm"
                data-cy="cancel-button"
                @click="cancelCashInventory"
              >
                {{ $t("button.cancel") }}
              </SButton>
              <SButton
                type="button"
                size="sm"
                data-cy="confirm-button"
                @click="confirmCashInventory"
              >
                {{ $t("button.confirm") }}
              </SButton>
            </div>
          </div>
        </SDialogContent>
      </SDialog>
    </div>

    <!-- Select cash closing amount destination -->
    <div v-if="showCashDestinationModal" class="cash-destination-modal-wrapper">
      <div class="cdm-overlay" @click.self="closeCashDestinationModal">
        <div class="cdm-container" data-cy="cash-destination-modal">
          <!-- Header -->
          <div class="cdm-header">
            <h3 class="cdm-title">
              {{ t("cashfloat.select-cash-destination") }}
            </h3>
          </div>

          <!-- Content -->
          <div class="cdm-body">
            <!-- Location Select -->
            <div class="cdm-form-group">
              <label class="cdm-label" for="destination-select">
                {{ t("cashfloat.cash-intended-to") }}
              </label>
              <select
                id="destination-select"
                v-model="selectedDestinationLocationId"
                :disabled="willMoneyStayInDrawer"
                class="cdm-select"
                data-cy="cash-destination-select"
              >
                <option :value="null" disabled>
                  {{ t("cashfloat.choose-location") }}
                </option>
                <option
                  v-for="item in locationsList"
                  :key="Number(item.id)"
                  :value="Number(item.id)"
                  data-cy="cash-destination-option"
                >
                  {{ String(item.name) }}
                </option>
              </select>
            </div>

            <!-- Checkbox -->
            <div class="cdm-checkbox-group">
              <label class="flex items-center gap-2">
                <SCheckbox
                  :model-value="willMoneyStayInDrawer"
                  @update:model-value="
                    (v) => {
                      willMoneyStayInDrawer = Boolean(v);
                      resetSelectedDestination();
                    }
                  "
                />
                <span>{{ $t("cashfloat.money-stays-in-the-drawer") }}</span>
              </label>
            </div>
          </div>

          <!-- Footer -->
          <div class="cdm-footer">
            <SButton
              type="button"
              variant="outline"
              :disabled="isSubmitting"
              data-cy="close-destination-modal"
              @click="closeCashDestinationModal"
            >
              {{ t("button.cancel") }}
            </SButton>
            <SButton
              type="button"
              :disabled="
                isSubmitting ||
                (!willMoneyStayInDrawer && !selectedDestinationLocationId)
              "
              data-cy="confirm-destination-button"
              @click="acceptAndClose"
            >
              {{ t("button.confirm") }}
            </SButton>
          </div>
        </div>
      </div>
    </div>

    <!-- youtube demo -->
    <div v-if="showYoutubeDemo" class="youtube-demo-modal-wrapper">
      <div class="cdm-overlay" @click.self="toggleYoutubeDemoModal(false)">
        <div class="cdm-container">
          <iframe
            src="https://www.youtube.com/embed/OKWcmlHnhBc?si=JSNU3fRW1ENLT_ID"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SInput,
  SInputGroup,
  SInputGroupTextarea,
} from "@smeetz/ui-core";
import CashFloatModule from "@/store/modules/cash-float";
import { LogLevel, LogService } from "@/services/logging";
import {
  allDetailedCurrencies,
  Currencies,
  detailedCurrency,
} from "@/constants/currency";
import { formatDateTime } from "@/utils/helpers/dates";
import { ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import {
  getManualClosingSummary,
  getDigitalClosingSummary,
  pushSyncCashfloat,
} from "@/api/cash-float/cash-float";
import {
  IManualClosingSummary,
  IDigitalClosingSummary,
  IPaymentMethodSummary,
  ICashFloat,
  ICashTransfer,
  CashAmounts,
} from "@/interfaces/cash-float/cash-float";
import { getCurrencySymbol } from "@/utils/vue-helpers/accounting";
import { PaymentMethods } from "@/types/db/accounting";
import POSConfigModule from "@/store/modules/pos-config";
import { printClosingSummaryReceipt } from "@/printing/handlers/receipts";
import {
  IClosingSummaryReceipt,
  IPaymentMethodSection,
  IReceiptLine,
} from "@/interfaces/receipts";
import { getGroupInfo } from "@/utils/vue-helpers/group";
import dayjs from "dayjs";
import { openCashDrawer as openCashDrawerUtil } from "@/utils/helpers/cross-platform";
import UserModule from "@/store/modules/user";
import { cloneDeep } from "lodash";
import NotificationService from "@/services/notification.service";
import DeviceModule from "@/store/modules/device";

import {
  ArrowUpDownIcon,
  BadgeDollarSignIcon,
  BadgeEuroIcon,
  BadgePoundSterlingIcon,
  BadgeSwissFrancIcon,
  BanknoteIcon,
  CheckCheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleXIcon,
  FileSpreadsheetIcon,
  NotebookPenIcon,
  RadioReceiverIcon,
  TriangleAlertIcon,
  YoutubeIcon,
} from "@smeetz/ui-icons";
import { ICashSummary } from "../../../interfaces/cash-float/cash-float";
import BookingModule from "@/store/modules/booking";
import {
  checkAndSyncIncompleteAdyenOrders,
  hasNonEmptyBookings,
} from "@/utils/vue-helpers/booking";
import {
  confirmClosingSales,
  handleCashTransferSync,
} from "@/utils/vue-helpers/cash-float";
import { generateID } from "@/utils/helpers/app";
import { IReceiptSection } from "../../../interfaces/receipts";
import { clearDexieDB } from "@/utils/vue-helpers/db";
import {
  mapCashLocation,
  processAdditionalEvents,
  processMoneyInOutEvent,
} from "@/utils/helpers/compliance";
import { EventType, MoneyInOutType } from "@smeetz/compliance-common";
import { PaymentMethod } from "@smeetz/order-common";

const { t } = useI18n({});

const showSyncModal = ref(true);
async function handleSyncSuccess(status: boolean) {
  if (!status) {
    // sync failed, notify the user and close
    cancelAndReturn();
  }
  showSyncModal.value = false;
  try {
    isLoading.value = true;
    await Promise.all([fetchManualSummary(), fetchDigitalSummary()]);
  } catch (error) {
    LogService.info(
      `Sale closure: Error fetching closing summary for cash float uuid: ${cashfloat.value?.uid}`,
      {
        error,
      }
    );
    const response = (error as { response?: { status?: number } })?.response;
    let message = t("error.general-error");

    if (response?.status === 404) {
      // A missing cash float means a Smeetz admin opened the session
      // which is never saved. Fire a Graylog alert.
      message = t("error.cashfloat-not-found");
      const cashfloatUid = cashfloat.value?.uid;
      const groupId = POSConfigModule.groupId;
      LogService.info(
        `Alert: cash float not found when loading the closing summary. uid=${cashfloatUid} groupId=${groupId}`,
        { params: { cashfloatUid, groupId } },
        LogLevel.Error
      );
    } else if (!response) {
      // The request never reached the server
      message = t("error.load-cashfloat");
    }

    NotificationService.showCustomMessage(message, "error");
    cancelAndReturn();
  } finally {
    isLoading.value = false;
  }
}

const hasLocation = computed(() => {
  // If there is no hardware location (machine not linked to a location or user using a browser) we don't show device settings
  if (
    !DeviceModule.hardwareLocation ||
    !DeviceModule.hardwareLocation.locationId
  ) {
    return false;
  } else {
    return true;
  }
});

const activeTab = ref("manual");
const isLoading = ref(true);
const isSubmitting = ref(false);
const manualSummary = ref<IManualClosingSummary | null>(null);
const digitalSummary = ref<IDigitalClosingSummary | null>(null);

const cashfloat = ref<ICashFloat | null>(CashFloatModule.cashFloat);

const selectedPayment = ref<IPaymentMethodSummary | null>(null);
const selectedCurrency = ref<string>("CHF");

// Cash rounding is a derived adjustment rather than a tender the operator
// counts, so it is left out of the closing breakdown entirely. It still counts
// towards the totals below — only its own row and detail panel are dropped.
const paymentMethods = computed<IPaymentMethodSummary[]>(
  () =>
    manualSummary.value?.amountsPerPaymentMethods.filter(
      (pm) => pm.name !== PaymentMethods.METHOD_CASH_ROUNDING
    ) || []
);

const activeCurrencies = ref<string[]>([]);
const groupCurrencies = computed(() => {
  return POSConfigModule.groupCurrencies;
});
const groupCurrency = computed<Currencies>(() => {
  const currency =
    groupCurrencies.value.find((c) => c.isDefault === 1) ||
    groupCurrencies.value[0];

  return currency.label;
});

const openedByName = computed(() => {
  if (manualSummary.value?.openedBy) {
    const { firstName, lastName } = manualSummary.value.openedBy;
    return `${firstName} ${lastName}`;
  }
  return "";
});
const sessionStartDate = computed(() => {
  if (manualSummary.value?.createdAt) {
    return formatDateTime(manualSummary.value.createdAt);
  }
  return formatDateTime(cashfloat.value?.createdAt || "");
});
const sessionOpenedMessagee = computed(() => {
  return t("cashfloat.session-opened-message", {
    datetime: sessionStartDate.value,
    user: openedByName.value,
  });
});

const showNoteDialog = ref(false);
const note = ref("");
const selectedPaymentForNote = ref<IPaymentMethodSummary | null>(null);
const selectedCurrencyForNote = ref<string | null>(null);

// a map of counted amounts per currency per payment method
const forMattedCountedAmounts = ref<{
  [key: string]: {
    [key: string]: string;
  };
}>({});

const currencyPanels = ref<HTMLElement | null>(null);

const showCashInventoryDialog = ref(false);
let detailedActiveCurrencies: detailedCurrency[] = [];
const selectedDetailedCurrency = ref<detailedCurrency | null>(null);

const locationsList = computed(() => {
  return DeviceModule.locations.filter(
    (l) => l.id !== Number(DeviceModule.hardwareLocation?.locationId)
  );
});
const showCashDestinationModal = ref(false);
const willMoneyStayInDrawer = ref(false);
const selectedDestinationLocationId = ref<number | null>(null);

const showYoutubeDemo = ref(false);
function toggleYoutubeDemoModal(show: boolean) {
  showYoutubeDemo.value = show;

  LogService.info(
    `Sale closure: User ${show ? "opened" : "closed"} the youtube demo`
  );
}

function resetSelectedDestination() {
  selectedDestinationLocationId.value = null;
}

function closeCashDestinationModal() {
  showCashDestinationModal.value = false;
}

function openCashInventory(currency: string) {
  // try to find it in detailedActiveCurrencies
  // if not found, find it in allDetailedCurrencies and clone it
  const existingDetailedCurrency = detailedActiveCurrencies.find(
    (c) => c.id === currency
  );
  let detailedCurrency =
    existingDetailedCurrency ||
    allDetailedCurrencies.find((c) => c.id === currency);

  if (!detailedCurrency) {
    LogService.info(
      `Sale closure: Detailed currency not found for ${currency}`
    );
    return;
  }

  selectedDetailedCurrency.value = cloneDeep(detailedCurrency);
  showCashInventoryDialog.value = true;
}

function confirmCashInventory() {
  if (
    !selectedDetailedCurrency.value ||
    !selectedPayment.value ||
    !selectedCurrency.value
  ) {
    return;
  }

  // add or replace it into detailedActiveCurrencies
  const index = detailedActiveCurrencies.findIndex(
    (c) => c.id === selectedDetailedCurrency.value?.id
  );
  if (index !== -1) {
    detailedActiveCurrencies[index] = selectedDetailedCurrency.value;
  } else {
    detailedActiveCurrencies.push(selectedDetailedCurrency.value);
  }

  // calculate counted amount
  const newCountedAmoumt = sumDetailedCurrency(selectedDetailedCurrency.value);
  // update counted amount
  if (!selectedPayment.value.countedAmounts) {
    selectedPayment.value.countedAmounts = {};
  }
  selectedPayment.value.countedAmounts[selectedCurrency.value] =
    newCountedAmoumt;
  forMattedCountedAmounts.value[selectedPayment.value.name][
    selectedCurrency.value
  ] = (newCountedAmoumt / 100).toFixed(2);

  showCashInventoryDialog.value = false;
}

function cancelCashInventory() {
  // reset selected detailed currency
  selectedDetailedCurrency.value = null;
  showCashInventoryDialog.value = false;
}

function sumDetailedCurrency(detailedCurrency: detailedCurrency): number {
  let sum = 0;

  // Bills: convert to cents (multiply by 100)
  for (let i = 0; i < detailedCurrency.bills.length; i++) {
    sum =
      sum +
      Number(detailedCurrency.bills[i].name) *
        Number(detailedCurrency.bills[i].userInput) *
        100;
  }

  // Coins: convert to cents (multiply by 100)
  for (let i = 0; i < detailedCurrency.coins.length; i++) {
    sum =
      sum +
      Number(detailedCurrency.coins[i].name) *
        Number(detailedCurrency.coins[i].userInput) *
        100;
  }

  // Cents: already in cents, no conversion needed
  for (let i = 0; i < detailedCurrency.cents.length; i++) {
    sum =
      sum +
      Number(detailedCurrency.cents[i].name) *
        Number(detailedCurrency.cents[i].userInput);
  }

  // Round to nearest cent to avoid floating point errors
  return Math.round(sum);
}

function scrollToCurrencySection(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
) {
  const sectionId = `currency-section-${paymentMethodSummary.name}-${currency}`;
  const target = document.getElementById(sectionId);

  if (currencyPanels.value && target) {
    const container = currencyPanels.value;
    const containerTop = container.getBoundingClientRect().top;
    const targetTop = target.getBoundingClientRect().top;

    const scrollOffset = targetTop - containerTop + container.scrollTop;

    container.scrollTo({
      top: scrollOffset,
      behavior: "smooth",
    });
  }
}

function shakeNoteContent(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
) {
  const el = document.getElementById(
    `note-content-${paymentMethodSummary.name}-${currency}`
  );
  if (el) {
    el.classList.remove("shake"); // reset animation
    void el.offsetWidth; // force reflow
    el.classList.add("shake");
  }
}

function openNoteDialog(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
) {
  note.value = paymentMethodSummary.notes?.[currency] || "";
  selectedPaymentForNote.value = paymentMethodSummary;
  selectedCurrencyForNote.value = currency;
  showNoteDialog.value = true;
}

function addNote(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
) {
  if (!paymentMethodSummary.notes) {
    paymentMethodSummary.notes = {};
  }
  paymentMethodSummary.notes[currency] = note.value;
  showNoteDialog.value = false;
  note.value = "";
  selectedPaymentForNote.value = null;
  selectedCurrencyForNote.value = null;
}

function getNote(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): string {
  return paymentMethodSummary.notes?.[currency] || "";
}

function formatCurrencyWithSymbol(amount: number, currency: Currencies) {
  if (!amount) return `${getCurrencySymbol(currency)} 0.00`;
  const formattedAmount = (amount / 100).toFixed(2);
  return `${getCurrencySymbol(currency)} ${formattedAmount}`;
}

function getInitialAmount(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  // only for CASH payment method
  if (paymentMethodSummary.name !== PaymentMethods.METHOD_POS_CASH) {
    return 0;
  }
  const initialAmount = paymentMethodSummary.startAmounts?.[currency] || 0;
  return initialAmount;
}

function getExpectedAmount(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  const expected = paymentMethodSummary.amounts?.[currency] || 0;
  return expected;
}

function getDifference(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  const counted = paymentMethodSummary.countedAmounts?.[currency] || 0;
  const expected = paymentMethodSummary.amounts?.[currency] || 0;
  return counted - expected;
}

function getCountedAmount(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  const counted = paymentMethodSummary.countedAmounts?.[currency] || 0;
  return counted;
}

function getCustomerAmountIn(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  const customerAmountIn =
    paymentMethodSummary.cutomerAmountIn?.[currency] || 0;
  return customerAmountIn;
}

function getRefund(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  const refund = paymentMethodSummary.refund?.[currency] || 0;
  return refund;
}

function getChange(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  // only for CASH payment method
  if (paymentMethodSummary.name !== PaymentMethods.METHOD_POS_CASH) {
    return 0;
  }
  const change = paymentMethodSummary.change?.[currency] || 0;
  return change;
}

function getSettlement(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  const payment = getCustomerAmountIn(paymentMethodSummary, currency);
  const refund = getRefund(paymentMethodSummary, currency);
  const change = getChange(paymentMethodSummary, currency);
  return payment + refund + change;
}

function getTransferIn(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  // only for CASH payment method
  if (paymentMethodSummary.name !== PaymentMethods.METHOD_POS_CASH) {
    return 0;
  }
  const transferIn = paymentMethodSummary.transferIn?.[currency] || 0;
  return transferIn;
}

function getTransferOut(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
): number {
  // only for CASH payment method
  if (paymentMethodSummary.name !== PaymentMethods.METHOD_POS_CASH) {
    return 0;
  }
  const transferOut = paymentMethodSummary.transferOut?.[currency] || 0;
  return transferOut;
}

// Methods for switching tabs and fetching data
const switchTab = (tab: "manual" | "digital") => {
  activeTab.value = tab;
};

// Fetch summary data from API
const fetchManualSummary = async () => {
  try {
    const cashfloatUuid = CashFloatModule.cashFloat?.uid;
    if (!cashfloatUuid) {
      LogService.info("Sale closure: No cashfloat UUID available");
      return;
    }

    const response = await getManualClosingSummary(cashfloatUuid);
    manualSummary.value = response;

    LogService.info(
      `Sale closure: Manual closing summary fetched for uuid: ${cashfloatUuid}`,
      {
        manualSummary: response,
      }
    );

    // Update active currencies
    if (response.activeCurrencies) {
      // make sure the main currency is first
      response.activeCurrencies.sort((a, b) =>
        a === groupCurrency.value ? -1 : 1
      );
      activeCurrencies.value = response.activeCurrencies;
    }

    // Initialize counted amounts, notes, and settlement directly within manualSummary
    if (manualSummary.value.amountsPerPaymentMethods) {
      response.amountsPerPaymentMethods.forEach((pm) => {
        pm.countedAmounts = pm.countedAmounts || {};
        pm.notes = pm.notes || {};
        pm.settlement = pm.settlement || {};

        // Calculate and store settlement for each currency
        for (const currency of response.activeCurrencies || []) {
          const payment = pm.cutomerAmountIn?.[currency] || 0;
          const refund = pm.refund?.[currency] || 0;
          const change = pm.change?.[currency] || 0;
          pm.settlement[currency] = payment + refund + change;
        }

        forMattedCountedAmounts.value[pm.name] = {};

        activeCurrencies.value.forEach((currency) => {
          const expectedAmountValue = pm.amounts?.[currency] || 0;

          // Initialize counted amount to expected if not already set (e.g., from user input)
          if (pm.countedAmounts && pm.countedAmounts[currency] === undefined) {
            pm.countedAmounts[currency] = expectedAmountValue || 0;
          }
          // Initialize notes if not already set
          if (pm.notes && pm.notes[currency] === undefined) {
            pm.notes[currency] = "";
          }

          // Initialize formatted counted amounts
          forMattedCountedAmounts.value[pm.name][currency] = (
            expectedAmountValue / 100
          ).toFixed(2);
        });
      });
    }

    // set selected payment method to the first one
    if (paymentMethods.value.length > 0) {
      selectedPayment.value = paymentMethods.value[0];
    }

    if (activeCurrencies.value.length > 0) {
      // set selected currency to the first one
      selectedCurrency.value = activeCurrencies.value[0];
    }
  } catch (error) {
    LogService.info(
      `Sale closure: Error fetching manual closing summary for uuid: ${CashFloatModule.cashFloat?.uid}`,
      { error }
    );
    throw error;
  }
};

const fetchDigitalSummary = async () => {
  try {
    const cashfloatUuid = cashfloat.value?.uid;
    if (!cashfloatUuid) {
      LogService.info("Sale closure: No cashfloat UUID available");
      return;
    }

    const response = await getDigitalClosingSummary(cashfloatUuid);
    digitalSummary.value = response;

    // Calculate and store settlement for digital payment methods
    if (digitalSummary.value) {
      digitalSummary.value.totalCreditCard.settlement =
        digitalSummary.value.totalCreditCard.payment +
        digitalSummary.value.totalCreditCard.refund;

      digitalSummary.value.totalVoucher.settlement =
        digitalSummary.value.totalVoucher.payment +
        digitalSummary.value.totalVoucher.refund;

      digitalSummary.value.totalGiftCard.settlement =
        digitalSummary.value.totalGiftCard.payment +
        digitalSummary.value.totalGiftCard.refund;

      digitalSummary.value.total.settlement =
        digitalSummary.value.total.payment + digitalSummary.value.total.refund;
    }

    LogService.info(
      `Sale closure: Digital closing summary fetched for uuid: ${cashfloatUuid}`,
      {
        digitalSummary: response,
      }
    );
  } catch (error) {
    LogService.info(
      `Sale closure: Error fetching digital closing summary for uuid: ${cashfloat.value?.uid}`,
      { error }
    );
    throw error;
  }
};

// Action handlers
const addCashTransfer = () => {
  LogService.info("Sale closure: Opening cash transfer modal");
  ModalModule.setStatus(ModalStatus.CashInOut);
};

const openCashDrawer = async () => {
  LogService.info("Sale closure: Opening cash drawer");
  try {
    openCashDrawerUtil();
  } catch (error) {
    LogService.info("Sale closure: Error opening cash drawer", { error });
  }
};

const cancelAndReturn = () => {
  LogService.info(
    `Sale closure: Canceling cash float close, uuid: ${cashfloat.value?.uid}`
  );
  CashFloatModule.setShowClosingSummary(false);
  CashFloatModule.setIsClosingCashfloat(false);
};

const acceptAndClose = async () => {
  isSubmitting.value = true;

  // Only prompt the open-bookings warning on the initial close action.
  if (hasNonEmptyBookings.value && !showCashDestinationModal.value) {
    LogService.info(
      "Sale closure: User closing cashfloat with incomplete/partially paid bookings, prompting for confirmation"
    );
    // if user has incomplete or partially paid bookings, a warning is shown
    const confirmed = await confirmClosingSales();
    if (!confirmed) {
      LogService.info("Sale closure: User canceled closing sales");
      isSubmitting.value = false;
      return;
    }
    LogService.info("Sale closure: User confirmed closing sales");
    // check if the incomplete bookings have an adyen payment method, if yes then these orders have to be synced
    const requiresSync = await checkAndSyncIncompleteAdyenOrders();
    if (requiresSync) {
      // make sure we do not close sales before sync is done, otherwise we might end up with orders that are closed but not synced
      LogService.info(
        "Sale closure: Incomplete bookings with Adyen payments detected, attempting to sync them"
      );
      showSyncModal.value = true;
    }
  }

  if (!cashfloat.value || !cashfloat.value.uid || !manualSummary.value) {
    LogService.info(
      `Sale closure: Cash float or summary data is missing when closing, uuid: ${cashfloat.value?.uid}`,
      {
        cashfloat: cashfloat.value,
        manualSummary: manualSummary.value,
      }
    );
    NotificationService.showCustomMessage(
      "Cash float or summary data is missing",
      "error"
    );
    isSubmitting.value = false;
    return;
  }

  const cashfloatUuid = cashfloat.value.uid;
  // Check if there are any differences that need to have a note
  // if we have a difference without a note, we scroll to the first one
  for (const pm of manualSummary.value.amountsPerPaymentMethods) {
    for (const currency of activeCurrencies.value) {
      if (getDifference(pm, currency) && !getNote(pm, currency)) {
        scrollToCurrencySection(pm, currency);
        // wait a bit and shake the note icon
        setTimeout(() => {
          shakeNoteContent(pm, currency);
        }, 500);
        isSubmitting.value = false;
        return;
      }
    }
  }

  const cashPaymentSummary = manualSummary.value.amountsPerPaymentMethods.find(
    (pm) => pm.name === PaymentMethods.METHOD_POS_CASH
  );

  // if the pos device is linked (cashbook logic), the user must select a destination location for the cash closing amount
  // skip the destination modal when counted cash is 0, there is no money to transfer
  const hasCashToTransfer =
    cashPaymentSummary && groupCurrency.value
      ? getCountedAmount(cashPaymentSummary, groupCurrency.value) !== 0
      : false;

  if (
    hasLocation.value &&
    hasCashToTransfer &&
    !willMoneyStayInDrawer.value &&
    !selectedDestinationLocationId.value
  ) {
    showCashDestinationModal.value = true;
    isSubmitting.value = false;
    return;
  }
  // for cashbook logic, if we have a difference in cash (counted vs expected), we need to justify it via a Gain/Loss cash transfer
  // cashbook logic is only for the main group currency (for now)
  if (hasLocation.value && groupCurrency.value && cashPaymentSummary) {
    const difference = cashPaymentSummary
      ? getDifference(cashPaymentSummary, groupCurrency.value)
      : 0;
    if (difference !== 0) {
      // so we have a difference that needs to be tracked via Gain/Loss cash transfer
      LogService.info(
        `Sale closure: There is cash difference when closing the cashfloat: ${cashfloatUuid} as attached`,
        {
          params: {
            currency: groupCurrency.value,
            expectedCashfloat: getExpectedAmount(
              cashPaymentSummary,
              groupCurrency.value
            ),
            finalManualCashfloat: getCountedAmount(
              cashPaymentSummary,
              groupCurrency.value
            ),
          },
        }
      );

      let isGain = difference > 0 ? true : false;

      let from = null;
      let to = null;
      if (isGain) {
        // cashIn to the device because it is a gain
        to =
          DeviceModule.hardwareLocation &&
          DeviceModule.hardwareLocation?.locationId
            ? Number(DeviceModule.hardwareLocation?.locationId)
            : null;
        from = null;
      } else {
        // cashOut from the device because it is a loss
        from =
          DeviceModule.hardwareLocation &&
          DeviceModule.hardwareLocation?.locationId
            ? Number(DeviceModule.hardwareLocation?.locationId)
            : null;
        to = null;
      }
      const amount = Math.abs(difference / 100);
      const cashTransferOut: ICashTransfer = {
        _id: dayjs().toISOString(),
        createdAt: dayjs().toISOString(),
        amount,
        currency: groupCurrency.value,
        groupId: Number(POSConfigModule.groupId),
        from: from,
        to: to,
        note: getNote(cashPaymentSummary, groupCurrency.value) || null,
        channel: "Smeetz POS",
        uuid: generateID(),
        isGainLoss: true,
      };

      if (!CashFloatModule.initialTransferDone) {
        LogService.info(
          `Sale closure: Adding Gain/Loss cash transfer for cash difference when closing the cashfloat: ${cashfloatUuid}`,
          {
            cashTransfer: cashTransferOut,
          }
        );
        const isSynced = await handleCashTransferSync(cashTransferOut);
        if (!isSynced) {
          isSubmitting.value = false;
          return;
        }

        // process money in/out event after gain/loss cash transfer
        try {
          const eventData = {
            moneyInOutType: MoneyInOutType.DIFFERENCE,
            amount: isGain ? amount : -amount,
            description: (isGain ? "GAIN" : "LOSS") + " cash transfer",
            paymentType: PaymentMethod.CASH,
            posDateTime: dayjs().toISOString(),
            cashLocation: mapCashLocation(null),
          };
          await processMoneyInOutEvent(eventData);
        } catch (error) {
          LogService.info("Failed process money in/out event", { error });
        }

        /**
         * Since next cash transfers (coming from user cashing out closing amount) can fail and user is asked to try again
         * This gain/loss transfer can be generated more than once
         * To resolve this, a flag is set to true when this transfer is done to not send it again
         * This flag is reset after all cash transfers are done
         */
        CashFloatModule.setInitialTransferDone(true);
      }
    }

    // User may want to cash out the full closing amount if his machine is linked
    // If there is no cash to transfer, treat it as cashed out, no money remains in the drawer
    let isCashfloatCashedOut = !hasCashToTransfer;
    if (!willMoneyStayInDrawer.value && selectedDestinationLocationId.value) {
      LogService.info(
        `Sale closure: User opted to cash out the closing amount for cashfloat: ${cashfloatUuid}`
      );
      const from =
        DeviceModule.hardwareLocation &&
        DeviceModule.hardwareLocation?.locationId
          ? Number(DeviceModule.hardwareLocation?.locationId)
          : null;
      const to = selectedDestinationLocationId.value;
      // Add a new CashTransfer of type (OUT)
      const amount = Math.abs(
        getCountedAmount(cashPaymentSummary, groupCurrency.value) / 100
      );
      const cashTransferOut: ICashTransfer = {
        _id: dayjs().toISOString(),
        createdAt: dayjs().toISOString(),
        amount,
        currency: groupCurrency.value /*Currencies.CHF*/,
        groupId: Number(POSConfigModule.groupId),
        from: from,
        to: to,
        note: "", // form.message || null,
        channel: "Smeetz POS",
        uuid: generateID(),
      };

      const isSynced = await handleCashTransferSync(cashTransferOut);
      if (!isSynced) {
        isSubmitting.value = false;
        return;
      }
      // process money in/out event after cash transfer
      try {
        const eventData = {
          moneyInOutType: MoneyInOutType.PAY_OUT,
          amount: -amount,
          description: "Cash transfer OUT",
          paymentType: PaymentMethod.CASH,
          posDateTime: dayjs().toISOString(),
          cashLocation: mapCashLocation(to),
        };
        await processMoneyInOutEvent(eventData);
      } catch (error) {
        LogService.info("Failed process money in/out event", { error });
      }

      isCashfloatCashedOut = true;
    }

    if (!isCashfloatCashedOut) {
      // user didn't cash out the cash closing amount
      // we need to keep this info, it will be used in the next cashfloat opening
      const closingAmounts: CashAmounts = {};
      // add amounts for all currencies (only for cash)
      for (const c of activeCurrencies.value) {
        closingAmounts[c] = getCountedAmount(cashPaymentSummary, c);
      }
      const cashfloatInfo = {
        uuid: cashfloatUuid,
        closingAmounts: closingAmounts,
        isCashedOut: false,
      };

      LogService.info(
        `Sale closure: User didn't cash out cashfloat closing amount. Setting cashfloat info attached to previousCashfloat property in the state: ${cashfloatUuid}`,
        {
          params: { previousCashfloat: cashfloatInfo },
        }
      );
      CashFloatModule.setPreviousCashFloat(cashfloatInfo);
    } else {
      LogService.info(
        `Sale closure: User cashed out full cashfloat closing amount. Setting null to previousCashfloat property in the state: ${cashfloatUuid}`
      );
      CashFloatModule.setPreviousCashFloat(null);
    }
  }

  // check and log if there is at least 1 difference
  for (const pm of manualSummary.value.amountsPerPaymentMethods) {
    for (const currency of activeCurrencies.value) {
      if (getDifference(pm, currency)) {
        LogService.info(
          `Sale closure: There is a difference when closing the cashfloat: ${cashfloatUuid}`
        );
        break;
      }
    }
  }

  try {
    // add closing summary to cash float
    const cashfloatCopy: ICashFloat = cloneDeep(cashfloat.value);
    cashfloatCopy.closingSummary = {
      manualClosingSummary: manualSummary.value,
      digitalClosingSummary: digitalSummary.value,
    };
    // add billsAndCoinsByCurrency details for closing if available
    if (detailedActiveCurrencies.length > 0) {
      if (!cashfloatCopy.billsAndCoinsByCurrency) {
        cashfloatCopy.billsAndCoinsByCurrency = {};
      }
      const closingBillsAndCoinsByCurrency: ICashSummary[] = [];
      detailedActiveCurrencies.forEach((dc) => {
        closingBillsAndCoinsByCurrency.push({
          currency: dc.id,
          bills: dc.bills.map((b) => ({
            name: String(b.name),
            userInput: b.userInput,
          })),
          coins: dc.coins.map((c) => ({
            name: String(c.name),
            userInput: c.userInput,
          })),
          cents: dc.cents.map((c) => ({
            name: String(c.name),
            userInput: c.userInput,
          })),
        });
      });

      cashfloatCopy.billsAndCoinsByCurrency.closing =
        closingBillsAndCoinsByCurrency;
    }

    // set closeDate
    cashfloatCopy.closeDate = dayjs().toISOString();
    CashFloatModule.setCashFloat(cashfloatCopy);

    LogService.info(
      `Sale closure: Final closing summary shape before closing cash float uuid: ${cashfloatUuid}`,
      {
        cashfloat: cashfloatCopy,
      }
    );

    // submit closing cash float
    LogService.info(
      `Sale closure: Submitting closed cashfloat, uuid: ${cashfloatUuid}`,
      {
        cashfloat: cashfloatCopy,
      }
    );
    try {
      await pushSyncCashfloat([cashfloatCopy]);
    } catch (error) {
      LogService.info(
        `Sale closure: Error submitting closed cashfloat, uuid: ${cashfloatUuid}`,
        {
          cashfloat: cashfloatCopy,
          error: error,
        }
      );
      throw error;
    }

    prepareAndPrintReceipt(cashfloatCopy);

    // Daily closure event
    try {
      await processAdditionalEvents(EventType.REPORT_DAILY, {
        date: dayjs().format("YYYY-MM-DD"),
      });
    } catch (error) {
      LogService.info("Error processing Daily Report event", {
        error,
      });
    }

    CashFloatModule.setIsClosingCashfloat(false);
    CashFloatModule.setShowClosingSummary(false);
    CashFloatModule.setCashFloat(null);

    LogService.info(
      `Sale closure: Cash float closed and cleared successfully, uuid: ${cashfloatUuid}`,
      {
        cashfloat: CashFloatModule.cashFloat,
      }
    );
    // Ensure that no booking is remaining when sales are closed
    BookingModule.clearAllBookings();

    // clear Dexie db bookings
    await clearDexieDB();
  } catch (error) {
    LogService.info(
      `Sale closure: Error closing cash float: ${cashfloatUuid}`,
      { error }
    );
    NotificationService.showCustomMessage(t("error.close-cashfloat"), "error");
  } finally {
    isSubmitting.value = false;
  }
};

async function prepareAndPrintReceipt(cashfloat: ICashFloat) {
  const seller = UserModule.user;
  const sellerName = `${seller.firstName} ${seller.lastName}`;
  const groupInfo = await getGroupInfo();

  // Reconciliation is about what the operator counted, so cash rounding is left
  // out here — there is nothing to count against a derived adjustment. It still
  // appears in the payments summary below.
  const paymentMethodSections: IPaymentMethodSection[] = [];
  for (const pm of paymentMethods.value) {
    for (const currency of activeCurrencies.value) {
      // only cash has multi currencies
      if (
        pm.name !== PaymentMethods.METHOD_POS_CASH &&
        currency !== groupCurrency.value
      ) {
        continue;
      }
      const section: IPaymentMethodSection = {
        title: t("payment-method-in-currency", {
          method: t(`payment-method.${pm.name}`),
          currency: currency,
        }),
        lines: [],
      };

      // special case for cash with main currency
      if (
        pm.name === PaymentMethods.METHOD_POS_CASH &&
        currency === groupCurrency.value
      ) {
        section.lines.push({
          label: t("cashfloat.payments"),
          value: (getCustomerAmountIn(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        section.lines.push({
          label: t("cashfloat.refunds"),
          value: (getRefund(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        section.lines.push({
          label: t("cashfloat.change"),
          value: (getChange(pm, currency) / 100).toFixed(2),
          currency: currency,
        });

        section.lines.push({
          label: t("cashfloat.settlement"),
          value: (getSettlement(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
          hasBorder: true,
        });

        section.lines.push({
          label: t("cashfloat.initial-amount"),
          value: (getInitialAmount(pm, currency) / 100).toFixed(2),
          currency: currency,
          marginTop: true,
        });
        section.lines.push({
          label: t("cashfloat.settlement"),
          value: (getSettlement(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        if (hasLocation.value) {
          section.lines.push({
            label: t("cashfloat.transfer-in"),
            value: (getTransferIn(pm, currency) / 100).toFixed(2),
            currency: currency,
          });
          section.lines.push({
            label: t("cashfloat.transfer-out"),
            value: (getTransferOut(pm, currency) / 100).toFixed(2),
            currency: currency,
          });
        }

        section.lines.push({
          label: t("cashfloat.expected-amount"),
          value: (getExpectedAmount(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
          marginTop: true,
        });
        section.lines.push({
          label: t("cashfloat.counted-amount"),
          value: (getCountedAmount(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
        });
        section.lines.push({
          label: t("cashfloat.difference"),
          value: (getDifference(pm, currency) / 100).toFixed(2),
          currency: currency,
          isBold: true,
          hasBorder: true,
        });

        const note = getNote(pm, currency);
        if (note) {
          section.lines.push({
            label: t("cashfloat.note"),
            value: note,
            currency: "",
          });
        }

        paymentMethodSections.push(section);

        continue;
      }

      section.lines.push({
        label: t("cashfloat.initial-amount"),
        value: (getInitialAmount(pm, currency) / 100).toFixed(2),
        currency: currency,
      });
      section.lines.push({
        label: t("cashfloat.payments"),
        value: (getCustomerAmountIn(pm, currency) / 100).toFixed(2),
        currency: currency,
      });
      section.lines.push({
        label: t("cashfloat.refunds"),
        value: (getRefund(pm, currency) / 100).toFixed(2),
        currency: currency,
      });
      section.lines.push({
        label: t("cashfloat.change"),
        value: (getChange(pm, currency) / 100).toFixed(2),
        currency: currency,
      });

      section.lines.push({
        label: t("cashfloat.settlement"),
        value: (getSettlement(pm, currency) / 100).toFixed(2),
        currency: currency,
      });

      if (hasLocation.value) {
        section.lines.push({
          label: t("cashfloat.transfer-in"),
          value: (getTransferIn(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
        section.lines.push({
          label: t("cashfloat.transfer-out"),
          value: (getTransferOut(pm, currency) / 100).toFixed(2),
          currency: currency,
        });
      }

      section.lines.push({
        label: t("cashfloat.expected-amount"),
        value: (getExpectedAmount(pm, currency) / 100).toFixed(2),
        currency: currency,
        isBold: true,
      });
      section.lines.push({
        label: t("cashfloat.counted-amount"),
        value: (getCountedAmount(pm, currency) / 100).toFixed(2),
        currency: currency,
        isBold: true,
      });
      section.lines.push({
        label: t("cashfloat.difference"),
        value: (getDifference(pm, currency) / 100).toFixed(2),
        currency: currency,
        isBold: true,
        hasBorder: true,
      });

      const note = getNote(pm, currency);
      if (note) {
        section.lines.push({
          label: t("cashfloat.note"),
          value: note,
          currency: "",
        });
      }

      paymentMethodSections.push(section);
    }
  }

  const paymentSummarySections: IReceiptSection[] = [];
  const paymentsSummaryLines: IReceiptLine[] = [];
  let totalPayments = 0;
  let totalRefunds = 0;
  // Main currency first
  for (const pm of manualSummary.value?.amountsPerPaymentMethods || []) {
    for (const currency of activeCurrencies.value) {
      // skip multi currencies
      if (currency !== groupCurrency.value) {
        continue;
      }

      totalPayments += getCustomerAmountIn(pm, currency);
      totalRefunds += getRefund(pm, currency);

      paymentsSummaryLines.push({
        label: t(`payment-method.${pm.name}`),
        columns: [
          { value: (getCustomerAmountIn(pm, currency) / 100).toFixed(2) },
          { value: (getRefund(pm, currency) / 100).toFixed(2) },
          { value: "0.00" },
        ],
      });
    }
  }

  // Add digital payment methods
  if (digitalSummary.value) {
    totalPayments +=
      digitalSummary.value.totalCreditCard.payment +
      digitalSummary.value.totalGiftCard.payment;

    totalRefunds +=
      digitalSummary.value.totalCreditCard.refund +
      digitalSummary.value.totalGiftCard.refund;

    paymentsSummaryLines.push({
      label: t(`cashfloat.credit_card`),
      columns: [
        {
          value: (digitalSummary.value.totalCreditCard.payment / 100).toFixed(
            2
          ),
        },
        {
          value: (digitalSummary.value.totalCreditCard.refund / 100).toFixed(2),
        },
        { value: "0.00" },
      ],
    });
    paymentsSummaryLines.push({
      label: t(`cashfloat.gift-cards`),
      columns: [
        {
          value: (digitalSummary.value.totalGiftCard.payment / 100).toFixed(2),
        },
        {
          value: (digitalSummary.value.totalGiftCard.refund / 100).toFixed(2),
        },
        { value: "0.00" },
      ],
    });
  }

  paymentSummarySections.push({
    headers: [`${t("currency")}: ${groupCurrency.value}`],
    lines: paymentsSummaryLines,
  });

  // Cash summary (Multi currency)
  const cashPm = manualSummary.value?.amountsPerPaymentMethods.find(
    (pm) => pm.name === PaymentMethods.METHOD_POS_CASH
  );
  if (cashPm) {
    for (const currency of activeCurrencies.value) {
      // skip main currency
      if (currency === groupCurrency.value) {
        continue;
      }

      const payments = getCustomerAmountIn(cashPm, currency);
      const refunds = getRefund(cashPm, currency);
      const change = getChange(cashPm, currency);

      paymentSummarySections.push({
        headers: [`${t("currency")}: ${currency}`],
        lines: [
          {
            label: t("payment-method.CASH"),
            columns: [
              { value: (payments / 100).toFixed(2) },
              { value: (refunds / 100).toFixed(2) },
              { value: (change / 100).toFixed(2) },
            ],
          },
        ],
      });
    }
  }

  const receiptData: IClosingSummaryReceipt = {
    cashfloatUid: { key: t("receipt.cash-float-id"), value: cashfloat.uid },
    businessId: {
      key: t("field.vat"),
      value: groupInfo.vatNumber || groupInfo.registrationNum,
    },
    openingDate: {
      key: t("receipt.opening-date"),
      value: formatDateTime(manualSummary.value?.createdAt || ""),
    },
    closingDate: {
      key: t("receipt.closing-date"),
      value: formatDateTime(cashfloat.closeDate || ""),
    },
    groupInfo,
    printDate: {
      key: t("receipt.printed-on"),
      value: formatDateTime(dayjs().toString()),
    },
    openendBy: {
      key: t("session.opened"),
      value: openedByName.value,
    },
    closedBy: {
      key: t("session.closed"),
      value: sellerName,
    },
    paymentSummary: {
      title: t("cashfloat.payments-summary"),
      headers: [
        t("receipt.method"),
        t("receipt.payments"),
        t("receipt.refunds"),
        t("receipt.change"),
      ],
      sections: paymentSummarySections,
    },
    paymentMethodSections,
    closureDetailsField: t("cashfloat.closure-details"),
    reconciliationOverviewField: t("cashfloat.reconciliation-overview"),
    salesDetailsField: t("cashfloat.reconciliation-details"),
  };

  if (hasLocation.value) {
    receiptData.device = {
      key: t("settings.device"),
      value: DeviceModule.hardwareLocation?.deviceName || "NA",
    };
  }

  LogService.info(
    `Sale closure: Closing summary receipt data prepared for uuid: ${cashfloat.uid}`,
    {
      receiptData,
    }
  );
  printClosingSummaryReceipt(receiptData);
  NotificationService.showCustomMessage(
    t("completeorder.print-receipts-success"),
    "success"
  );
}

function handleFocus(event: FocusEvent) {
  const input = event.target as HTMLInputElement;
  input.select();
}

function onCountedAmountBlur(
  paymentMethod: IPaymentMethodSummary,
  currency: string
) {
  const raw = forMattedCountedAmounts.value[paymentMethod.name][currency];
  const parsed = parseFloat(raw.replace(",", "."));

  if (paymentMethod.countedAmounts === undefined) {
    paymentMethod.countedAmounts = {};
  }

  if (isNaN(parsed)) {
    forMattedCountedAmounts.value[paymentMethod.name][currency] = "0.00";
    paymentMethod.countedAmounts[currency] = 0;
    return;
  }

  const formatted = parsed.toFixed(2);
  forMattedCountedAmounts.value[paymentMethod.name][currency] = formatted;
  paymentMethod.countedAmounts[currency] = Math.round(parsed * 100);
}

function incrementAmount(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
) {
  if (!paymentMethodSummary.countedAmounts) {
    paymentMethodSummary.countedAmounts = {};
  }
  const current = paymentMethodSummary.countedAmounts?.[currency] || 0;
  paymentMethodSummary.countedAmounts[currency] = current + 100;
  forMattedCountedAmounts.value[paymentMethodSummary.name][currency] = (
    (current + 100) /
    100
  ).toFixed(2);
}

function decrementAmount(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
) {
  if (!paymentMethodSummary.countedAmounts) {
    paymentMethodSummary.countedAmounts = {};
  }
  const current = paymentMethodSummary.countedAmounts?.[currency] || 0;
  paymentMethodSummary.countedAmounts[currency] = Math.max(current - 100, 0);
  forMattedCountedAmounts.value[paymentMethodSummary.name][currency] = (
    (current - 100) /
    100
  ).toFixed(2);
}

// Open details view for a specific currency and payment method
function viewDetails(
  paymentMethodSummary: IPaymentMethodSummary,
  currency: string
) {
  selectedPayment.value = paymentMethodSummary;
  selectedCurrency.value = currency;
}

function getCurrencyIcon(currency: string) {
  switch (currency) {
    case "EUR":
      return BadgeEuroIcon;
    case "CHF":
      return BadgeSwissFrancIcon;
    case "USD":
      return BadgeDollarSignIcon;
    case "GBP":
      return BadgePoundSterlingIcon;
    default:
      return null;
  }
}

function handleInputFocus(event: any) {
  if (event.target.value === "0") {
    event.target.value = "";
  }
}

function handleInputBlur(event: any) {
  if (event.target.value === "") {
    event.target.value = "0";
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.online-cashfloat-closure {
  color: #111827;
  background-color: #ffffff;
  width: 100%;
  margin: 0 auto;
  /* These properties make the parent container take full viewport height */
  min-height: 90vh;
  display: flex;
  flex-direction: column;

  .main-content {
    padding: 24px;
    padding-bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    /* This makes the main content take up all available space */
    flex: 1 0 auto;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    width: 100%;
    font-size: 18px;
    color: #6b7280;
  }

  .title-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }

  .title {
    font-size: 24px;
    font-weight: 600;
    line-height: 30px;
    margin: 0;
    color: #000000;
    width: 100%;
  }

  .session-info {
    padding: 4px;
    background: #f7f7f7;
    border-radius: 2px;
    font-size: 14px;
    line-height: 18px;
    color: #939393;
    margin: 0;
    width: fit-content;
  }

  .action-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
  }

  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    gap: 8px;
    padding: 12px 16px;
    border-radius: 6px;
    font-weight: 500;
    font-size: 18px;
    line-height: 23px;
    cursor: pointer;
    border: none;
  }

  .btn[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-icon {
    width: 20px;
    height: 20px;
  }

  .note-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    white-space: nowrap;
  }

  .btn-outline {
    background-color: #fafafa;
    border: 1px solid #e5e7eb;
    color: #111827;
  }

  .btn-primary {
    background-color: var(--primary-smeetz);
    border: 1px solid var(--primary-smeetz);
    color: #ffffff;
  }

  .btn-large {
    padding: 12px 16px;
    font-size: 18px;
  }

  .btn-full {
    width: 100%;
  }

  .btn-white {
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    color: #111827;
  }
  .btn-selected {
    background-color: #f0faff;
    border: 1px solid #0ea5e9;
    color: #0ea5e9;
  }
  .btn-error {
    background-color: #fef2f2;
    border: 1px solid #ef4444;
    color: #ef4444;
  }

  .btn-small {
    height: 30px;
    font-size: 14px;
  }

  .tabs {
    display: flex;
    background-color: #fafafa;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 6px;
    gap: 12px;
    width: fit-content;
    margin: 0 auto;
  }

  .tab-button {
    padding: 12px 16px;
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    font-size: 18px;
    line-height: 23px;
    color: #111827;
  }

  .tab-button.active {
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
  }

  .content-row {
    display: flex;
    gap: 12px;
  }

  .cash-details-panel {
    flex: 1;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .digital-details-panel {
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .cash-details-panel .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background-color: #fff;
    border: none;
  }

  .cash-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 36px;

    overflow-y: auto;
    max-height: 60vh;
  }

  .currency-section {
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background-color: #fafafa;
    border-bottom: 1px solid #e5e7eb;
  }

  .digital-methods {
    width: 60%;
    min-width: 700px;
    margin: 0 auto;
    height: auto;
  }
  .digital-details-panel .panel-header {
    background-color: #f0fdff;
    padding: 24px 12px;
    border: 1px solid #e5e7eb;
  }

  .digital-methods .detail-item {
    padding: 12px;
  }
  .border-bottom {
    border-bottom: 1px solid #f1f2f4;
  }

  .panel-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    font-size: 18px;
    color: #000000;
  }
  .panel-title.large-title {
    font-size: 22px;
  }

  .panel-icon {
    width: 20px;
    height: 20px;
  }

  .currency-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 2px;
    font-weight: 800;
    font-size: 14px;
    border: 1.5px solid var(--primary-smeetz);
    color: var(--primary-smeetz);
  }

  .info-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
  }

  .info-button img {
    width: 16px;
    height: 16px;
  }

  .status-badge {
    padding: 6px 8px;
    border-radius: 2px;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
  }

  .matched {
    background-color: #daffe8;
    border: 1px solid #22c55e;
    color: #22c55e;
  }

  .unmatched {
    background-color: #fff5f5;
    border: 1px solid #ef4444;
    color: #ef4444;
  }

  .details-list {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .detail-label {
    color: #6b7280;
    font-size: 16px;
    font-weight: 500;
  }

  .detail-value {
    font-weight: 700;
    font-size: 16px;
  }

  .detail-value.highlight {
    color: var(--primary-smeetz);
  }

  .detail-value-left {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 60px;
  }

  .currency-content {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .currency-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .currency-label {
    color: #6b7280;
    font-size: 14px;
    line-height: 18px;
    display: flex;
    align-items: center;
  }

  .currency-info-button {
    margin: 0 4px;
  }

  .currency-info-button img {
    width: 16px;
    height: 16px;
  }

  .currency-value {
    font-weight: 700;
    font-size: 20px;
    line-height: 25px;
  }

  .amount-input-container {
    position: relative;
    display: flex;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
    align-items: center;
    justify-content: space-between;
  }

  .amount-input {
    width: 100%;
    padding: 12px;
    border: none;
    font-size: 14px;
    line-height: 17px;
    font-weight: 500;
    color: #111827;
  }

  .input-arrows {
    border-left: 1px solid #e5e7eb;
  }

  .arrow-up,
  .arrow-down {
    background-color: #fafafa;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 29px;
    height: 20px;
  }

  .arrow-up img,
  .arrow-down img {
    height: 100%;
    width: auto;
    // width: 12px;
    // height: 12px;
  }

  .arrow-down img {
    transform: rotate(180deg);
  }

  .mt-4 {
    margin-top: 16px;
  }

  .closure-footer {
    margin-top: auto;
    padding: 24px;
    padding-top: 12px;
    background-color: #ffffff;
    border-top: 1px solid #e5e7eb;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .warning-text {
    width: 50%;
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .warning-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  .warning-text p {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
  }

  .footer-buttons {
    width: 50%;
    display: flex;
    justify-content: flex-end;
    gap: 16px;
  }
  .footer-buttons .btn {
    flex: 1;
  }

  .red {
    color: #ef4444;
  }

  .green {
    color: var(--primary-smeetz);
  }

  .w-70 {
    width: 70%;
  }

  .font-weight-500 {
    font-weight: 500;
  }
  .font-weight-700 {
    font-weight: 700;
  }

  .f-medium {
    font-size: 16px;
  }
  .f-large {
    font-size: 20px;
  }

  .cash-destination-modal-wrapper {
    .cdm-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      backdrop-filter: blur(4px);
      animation: overlay-fade-in 0.2s ease-out;
    }

    @keyframes overlay-fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .cdm-container {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04);
      width: 90%;
      max-width: 480px;
      animation: modal-slide-up 0.3s ease-out;
    }

    @keyframes modal-slide-up {
      from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .cdm-header {
      padding: 24px 24px 16px;
      border-bottom: 1px solid #e5e7eb;
    }

    .cdm-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #111827;
      text-align: center;
    }

    .cdm-body {
      padding: 24px;
    }

    .cdm-form-group {
      margin-bottom: 20px;
    }

    .cdm-label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: #374151;
      text-align: left;
    }

    .cdm-select {
      width: 100%;
      padding: 10px 12px;
      font-size: 15px;
      color: #111827;
      background-color: white;
      border: 1.5px solid #d1d5db;
      border-radius: 8px;
      transition: all 0.2s ease;
      cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-size: 12px;
      padding-right: 36px;

      &:hover:not(:disabled) {
        border-color: #9ca3af;
      }

      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      &:disabled {
        background-color: #f9fafb;
        color: #9ca3af;
        border-color: #e5e7eb;
        cursor: not-allowed;
        opacity: 0.6;
      }

      // Placeholder styling
      option[value=""] {
        color: #9ca3af;
      }
    }

    .cdm-checkbox-group {
      padding: 16px;
      background-color: #f9fafb;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }

    .cdm-footer {
      padding: 16px 24px 24px;
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    }

    /* Responsive */
    @media (max-width: 640px) {
      .cdm-container {
        width: 95%;
        max-width: none;
        margin: 16px;
      }

      .cdm-header,
      .cdm-body,
      .cdm-footer {
        padding-left: 20px;
        padding-right: 20px;
      }

      .cdm-footer {
        flex-direction: column-reverse;

        .el-button {
          width: 100%;
        }
      }
    }
  }

  .youtube-demo-modal-wrapper .cdm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }

  .youtube-demo-modal-wrapper .cdm-container {
    width: 60vw;
    max-width: 1200px;
    aspect-ratio: 16 / 9;
    background: black;
    border-radius: 8px;
    overflow: hidden;
  }

  .youtube-demo-modal-wrapper iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  .watch-video-btn {
    width: 200px;
    height: 30px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(
      293.16deg,
      var(--primary-smeetz) 15.54%,
      #73cad7 86.43%
    );
    color: white;
    font-size: 14px;
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-family: system-ui, -apple-system, sans-serif;
    word-break: keep-all;

    &:hover {
      background-color: #4fa5a5;
    }

    .video-icon {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-4px);
  }
  40% {
    transform: translateX(4px);
  }
  60% {
    transform: translateX(-4px);
  }
  80% {
    transform: translateX(4px);
  }
  100% {
    transform: translateX(0);
  }
}

.shake {
  animation: shake 0.4s ease-in-out;
}
</style>
